import type { Project } from '@servicetim/shared';
import { useExportProjectTemplate } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { downloadJson, slugify } from '../lib/download';
import { FormRow } from '../ui/form-row';

export interface ExportTemplateDialogProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Saves the project's configuration as a template and downloads the JSON document. */
export function ExportTemplateDialog({ project, open, onOpenChange }: ExportTemplateDialogProps) {
  const id = useId();
  const exportTemplate = useExportProjectTemplate();
  const [name, setName] = useState(`${project.name} template`);
  const [description, setDescription] = useState('');

  const submit = async () => {
    if (name.trim().length === 0) return;
    try {
      const template = await exportTemplate.mutateAsync({
        id: project.id,
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
      });
      downloadJson(`${slugify(template.name)}.template.json`, template.document);
      toast.success('Template saved and downloaded', {
        description: 'Find it in the template center to create new projects from it.',
      });
      onOpenChange(false);
    } catch {
      // banner
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setName(`${project.name} template`);
          setDescription('');
          exportTemplate.reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Export as template</DialogTitle>
          <DialogDescription>
            Issue types, workflows, fields, forms, roles, grants, shared views, SLA policies and
            canned responses are exported with portable references (no ids or tenant data). The
            template is saved in the{' '}
            <Link to="/templates" className="text-accent underline">
              template center
            </Link>{' '}
            and downloaded as JSON.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {exportTemplate.error ? <ProblemBanner error={exportTemplate.error} size="sm" /> : null}
          <FormRow label="Template name" htmlFor={`${id}-name`} required>
            <Input
              id={`${id}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </FormRow>
          <FormRow label="Description" htmlFor={`${id}-description`}>
            <Input
              id={`${id}-description`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={exportTemplate.isPending || name.trim().length === 0}>
              Export
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
